import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * SignalPopoverRow — a single row inside the PDP "Why it scored" grid.
 * Clicking/hovering the row surfaces a Popover with the signal description,
 * weighted contribution, and an 8-week deterministic sparkline trend.
 */
export const SignalPopoverRow: React.FC<{
  index: number;
  label: string;
  description: string;
  pct: number;         // 0..100
  weight: number;      // per-signal % weight in the composite score
  contribution: number; // signed integer — points added / subtracted
  bar: string;         // tailwind bg class for the bar fill
  seedKey: string;     // stable string used to seed the sparkline
}> = ({ index, label, description, pct, weight, contribution, bar, seedKey }) => {
  // Deterministic hash → 8 pseudo-random walk points converging toward `pct`.
  // This gives every product+signal combination a distinct-looking sparkline
  // that stays consistent across re-renders (no useState/useEffect needed).
  const points = React.useMemo(() => {
    let h = 0;
    for (let i = 0; i < seedKey.length; i++) h = (h * 31 + seedKey.charCodeAt(i)) >>> 0;
    const rand = () => {
      h = (h * 1664525 + 1013904223) >>> 0;
      return (h & 0xffff) / 0xffff;
    };
    // Start ~25pts below the current value and walk toward it with jitter,
    // capping at [0, 100] so lines never leave the box.
    let v = Math.max(5, Math.min(95, pct - 20));
    const out: number[] = [];
    for (let i = 0; i < 8; i++) {
      const target = pct;
      const step = (target - v) * 0.35 + (rand() - 0.5) * 14;
      v = Math.max(4, Math.min(96, v + step));
      out.push(v);
    }
    // Final point locks to the current value for accuracy.
    out[out.length - 1] = pct;
    return out;
  }, [seedKey, pct]);

  const w = 120;
  const h = 34;
  const stepX = w / (points.length - 1);
  const path = points
    .map((y, i) => `${i === 0 ? "M" : "L"}${(i * stepX).toFixed(1)},${(h - (y / 100) * h).toFixed(1)}`)
    .join(" ");
  const areaPath = `${path} L${w},${h} L0,${h} Z`;
  const stroke =
    pct >= 80 ? "#10b981" :
    pct >= 60 ? "#f97316" :
                "#f59e0b";
  const contribDisplay = contribution === 0
    ? "±0"
    : (contribution > 0 ? `+${contribution}` : `${contribution}`);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`pdp-signal-${index}`}
          className="text-left cursor-pointer group focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sb-saffron)] focus-visible:ring-offset-2 rounded-md"
        >
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-slate-600 group-hover:text-slate-900 transition-colors">{label}</span>
            <span className="font-semibold text-slate-900 tabular-nums">{pct}</span>
          </div>
          <div className="h-1 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full ${bar}`} style={{ width: `${pct}%` }} />
          </div>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-72 p-4 rounded-xl border border-[color:var(--sb-border)] shadow-xl bg-white"
        data-testid={`pdp-signal-popover-${index}`}
      >
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="text-sm font-semibold text-slate-900 leading-tight">{label}</div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--sb-saffron)] font-bold tabular-nums shrink-0">
            {weight}% weight
          </div>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed mb-3">{description}</p>

        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3 mb-3">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last 8 weeks</div>
            <div
              className={`text-xs font-mono tabular-nums font-semibold ${
                contribution > 0 ? "text-emerald-600" : contribution < 0 ? "text-rose-600" : "text-slate-500"
              }`}
              data-testid={`pdp-signal-popover-contribution-${index}`}
            >
              {contribDisplay} pts
            </div>
          </div>
          <svg
            viewBox={`0 0 ${w} ${h}`}
            width="100%"
            height={h}
            className="overflow-visible"
            role="img"
            aria-label={`${label} trend for the last 8 weeks`}
            data-testid={`pdp-signal-popover-sparkline-${index}`}
          >
            <defs>
              <linearGradient id={`spark-fill-${index}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stroke} stopOpacity="0.25" />
                <stop offset="100%" stopColor={stroke} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={areaPath} fill={`url(#spark-fill-${index})`} />
            <path d={path} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round" />
            {points.map((y, i) => (
              <circle
                key={i}
                cx={i * stepX}
                cy={h - (y / 100) * h}
                r={i === points.length - 1 ? 2.5 : 1.4}
                fill={stroke}
              />
            ))}
          </svg>
          <div className="mt-2 flex items-center justify-between text-[10px] text-slate-400">
            <span>8w ago</span>
            <span className="font-semibold text-slate-600 tabular-nums">Now {pct}</span>
          </div>
        </div>

        <div className="text-[10px] text-slate-400 leading-relaxed">
          Contribution is measured against a neutral 65-signal baseline.
          Composite refreshes every 24h.
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const SIGNAL_DESCRIPTIONS: Record<string, string> = {
  "Customer Sentiment":     "Aggregated review + comment tone across all connected platforms.",
  "Review Volume":          "Absolute number of authentic reviews — more depth = more confidence.",
  "Star-Rating Consistency":"How consistent star ratings are across marketplaces (high variance drops the score).",
  "Complaint Rate":         "Ratio of complaint-classified comments vs. total. Lower is better.",
  "Verified Brand":         "Verified brand accounts on 2+ platforms boost this signal.",
  "Cross-Platform Mentions":"How widely the product is discussed beyond its own store.",
  "Creator Endorsements":   "Reviews and mentions from creators SociBaba considers reputable.",
  "Return Rate":            "Product returns reported by connected marketplaces.",
  "Price Value":            "Perceived value based on price vs. sentiment and category benchmarks.",
  "Product Longevity":      "How long the product has been available with sustained positive signal.",
  "Support Responsiveness": "Brand's reply latency + resolution rate on comments and complaints.",
};
